import { defineComponent, type SlotsType, h, type PropType, ref, onBeforeUnmount, watch, computed } from 'vue';
import { type ToastRegistry, type Toast, ToastKind } from '../../Types/Toast';
import { useToasts } from '../../Services/ToastManager';

const order = {
  [ToastKind.DANGER]: 0,
  [ToastKind.WARNING]: 1,
  [ToastKind.SUCCESS]: 2,
  [ToastKind.INFO]: 3,
};

export const ToastControllerComponent = defineComponent({
  name: 'ToastController',

  slots: Object as SlotsType<{
    default: { toasts: ToastRegistry },
  }>,

  // _props is a hack to avoid TS error on unused parameter as it is not used
  // at the moment it will throw a TS error, but it has to be passed to read
  // slots and attrs. Remember _ in front whenever you will actually have to
  // use the props. https://github.com/microsoft/TypeScript/issues/9458
  setup(_props, { slots, attrs }) {
    const toasts = useToasts();

    const sorted = computed(() => {
      return toasts.value.sort((a, b) => {
        return order[a.kind] - order[b.kind];
      });
    });

    return () => h('div', attrs, slots.default({ toasts: sorted.value }));
  },
});

export const ToastComponent = defineComponent({
  name: 'Toast',
  props: {
    toast: { type: Object as PropType<Toast>, required: true },
    position: { type: Number, required: true },
  },
  slots: Object as SlotsType<{
    default: { toast: Toast, close: () => void, closeAll: () => void },
  }>,
  setup(props, { slots, attrs }) {
    const toasts = useToasts();
    const timeout = ref();

    function close() {
      clearTimeout(timeout.value);
      toasts.value = toasts.value.filter((toast) => toast.id !== props.toast.id);
    }

    function closeAll() {
      clearTimeout(timeout.value);
      toasts.value = [];
    }

    onBeforeUnmount(() => {
      clearTimeout(timeout.value);
    });

    watch(() => props.position, (position) => {
      if (position === 0 && props.toast.kind === ToastKind.SUCCESS) {
        timeout.value = setTimeout(() => close(), props.toast.duration * 1000);
      } else {
        clearTimeout(timeout.value);
      }
    }, {
      immediate: true,
    });

    return () => h('li', attrs, slots.default({ toast: props.toast, close, closeAll }));
  },
});
